import type { TimesheetStatus } from '@/types';

/**
 * Tempo design tokens: Ink & Coral, with Iris as the fourth color.
 *
 * Every color in the app should come from here, either directly (inline styles, charts)
 * or through the antd theme (tempoTheme.ts) and the CSS variables (cssVars.ts) built from it.
 *
 * Roles (coral-led, v2.11.2):
 *  - Ink: structure and selection (sidebar, headings, text, money, checked controls, selected
 *    toggles, focus rings, info notes)
 *  - Coral (the logo's #F77D69): the brand accent (primary buttons with ink text, links, tabs,
 *    active page, avatars and icon chips in soft coral, the metric that needs action)
 *  - Iris: only the "Submitted" status and the second chart series (awaiting approval, billing)
 *  - Status colors: state only (draft, submitted, changes requested, approved, rejected)
 */
export const color = {
  // brand
  ink: '#171614',
  coral: '#F77D69',
  coralHover: '#F26650',
  coralActive: '#E85F48',
  coralText: '#B23F2B', // coral used as text on light surfaces: links, icons (5.8:1)
  coralDeep: '#9A3524', // link hover, avatar initials (6.1:1 on coralSoft)
  coralSoft: '#FDE8E3', // avatars, icon chips, text selection
  coralTint: '#FEF4F1', // selected table rows
  iris: '#6E56CF',
  irisDeep: '#5841B5', // deeper iris step, not used for links or controls since v2.11.2 (7.4:1 on white)
  irisDarker: '#4A36A0',
  irisSoft: '#EFECFC',
  irisTint: '#F5F3FE',

  // neutrals (slightly warm, to sit with coral)
  canvas: '#F3F3F1',
  surface: '#FFFFFF',
  surfaceAlt: '#F7F7F5',
  fill: '#F1F0EC',
  border: '#E4E3DF',
  borderStrong: '#CFCDC7',
  divider: '#EDECE8',
  text: '#171614',
  textSecondary: '#4A4843',
  textMuted: '#6B6861',
  textDisabled: '#9D9A92',

  // sidebar
  sidebar: '#171614',
  sidebarText: '#D6D4CE',
  sidebarMuted: '#9D9A92',
  sidebarHover: '#211F1C',
  sidebarActive: '#2A2825',
  sidebarBorder: '#2A2825',
  sidebarLine: '#3A3834', // outlines that must stay visible on ink
  white: '#FFFFFF', // text and icons on ink, iris, green or other dark fills

  // semantic
  success: '#2E9A52',
  successText: '#1A6A35',
  successSoft: '#E2F3E7',
  warning: '#D08A0E',
  warningText: '#86470A',
  warningSoft: '#FCF0D9',
  danger: '#E11D48',
  dangerText: '#9F1239',
  dangerSoft: '#FCE7EB',
  approve: '#1A6A35', // Approve button fill, white text 6.7:1
  approveHover: '#155A2C',

  // avatars
  avatarBg: '#FDE8E3',
  avatarText: '#9A3524',
} as const;

/** Floating panels (notification list and other custom popovers); cards stay flat. */
export const shadow = {
  popover: '0 8px 28px rgba(23, 22, 20, 0.12)',
} as const;

export const font = {
  ui: "'Archivo', 'Segoe UI', system-ui, -apple-system, sans-serif",
  // headings and big numbers use Archivo slightly expanded, echoing the wide "SYSTEMS" wordmark
  displayStretch: '112%',
} as const;

export interface StatusStyle {
  label: string;
  bg: string;
  text: string;
  dot: string;
}

export const statusStyle: Record<TimesheetStatus, StatusStyle> = {
  DRAFT: { label: 'Draft', bg: '#F1F0EC', text: '#4A4843', dot: '#9D9A92' },
  SUBMITTED: { label: 'Submitted', bg: color.irisSoft, text: color.irisDarker, dot: color.iris },
  CHANGES_REQUESTED: { label: 'Changes requested', bg: color.warningSoft, text: color.warningText, dot: color.warning },
  APPROVED: { label: 'Approved', bg: color.successSoft, text: color.successText, dot: color.success },
  REJECTED: { label: 'Rejected', bg: color.dangerSoft, text: color.dangerText, dot: color.danger },
};

export const statusOptions = (Object.keys(statusStyle) as TimesheetStatus[]).map((value) => ({
  value,
  label: statusStyle[value].label,
}));

/** Chart colors. Status series use status colors; pay vs billing uses coral vs iris. */
export const chart = {
  approved: color.success,
  pending: color.iris,
  draft: '#B8B4AB',
  rejected: color.danger,
  pay: '#EC6A53', // coral deepened just enough for 3:1 against white
  billing: color.iris,
  profit: color.success,
  loss: color.danger,
  warning: color.warning,
  grid: '#ECEBE7',
  axis: color.borderStrong,
  tick: color.textMuted,
} as const;

export const radius = { sm: 4, md: 6, lg: 8 } as const;
